import { useQuery } from '@tanstack/react-query';
import { EquipmentApi } from '@api/enterprise/openapi';
import { enterpriseApiClient } from '@api/enterprise/client';
import { useUserStore } from '@stores/userStore';
import type { EquipmentViewModel } from '@api/enterprise/openapi/models';

export const equipmentRecordKeys = {
  byId: (companyName: string, equipmentId: string) =>
    ['equipment', companyName, equipmentId] as const
};

export const fetchEquipmentRecord = async (
  companyName: string,
  equipmentId: string
): Promise<EquipmentViewModel | null> => {
  const equipmentApi = new EquipmentApi(undefined, undefined, enterpriseApiClient);

  try {
    const response = await equipmentApi.apiEquipmentCompanyNameIdGet({
      companyName,
      id: equipmentId
    });
    return response.data;
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 404) {
        return null;
      }
    }
    return null;
  }
};

export const useEquipmentRecord = (
  equipmentId: string,
  equipmentCompanyName?: string,
  enabled = true
) => {
  const storeCompanyName = useUserStore(state => state.employeeCompany) ?? '';
  const companyName = equipmentCompanyName || storeCompanyName;

  return useQuery<EquipmentViewModel | null, Error>({
    queryKey: equipmentRecordKeys.byId(companyName, equipmentId),
    queryFn: () => fetchEquipmentRecord(companyName, equipmentId),
    enabled: enabled && !!equipmentId && !!companyName,
    staleTime: 5 * 60 * 1000
  });
};
